import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Coins, ShoppingCart, Sparkles, Crown, CheckCircle, XCircle, Loader2, Lock, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { getIAPService, IAP_PRODUCTS } from "@/services/AppleIAPService";

// Format coins for display
const formatCoins = (amount) => {
  if (amount >= 1000000) {
    const m = amount / 1000000;
    return m % 1 === 0 ? `${m.toFixed(0)}M` : `${m.toFixed(1)}M`;
  }
  if (amount >= 1000) {
    const k = amount / 1000;
    return k % 1 === 0 ? `${k.toFixed(0)}K` : `${k.toFixed(1)}K`;
  }
  return amount.toLocaleString();
};

// ADE Coins Shop Component
export const CoinShop = ({ isOpen, onClose, onPurchaseComplete }) => {
  const [packages, setPackages] = useState([]);
  const [userGoldenLevel, setUserGoldenLevel] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const iapService = getIAPService();
  const isNativeIOS = iapService.isAvailable();
  
  useEffect(() => {
    if (isOpen) {
      fetchPackages();
      if (isNativeIOS) iapService.initialize();
    }
  }, [isOpen]);
  
  const fetchPackages = async () => {
    try {
      const response = await api.get('/shop/packages');
      setPackages(response.data.packages || []);
      setUserGoldenLevel(response.data.user_golden_level || 0);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error("Errore nel caricamento dei pacchetti");
    } finally {
      setLoading(false);
    }
  };
  
  const handlePurchase = async (pkg) => {
    if (!pkg.available) {
      toast.error(pkg.locked_message || "Pacchetto non disponibile");
      return;
    }
    
    setPurchasing(pkg.id);
    
    try {
      if (isNativeIOS) {
        // Apple IAP flow
        const iapProductId = IAP_PRODUCTS.find(p => p.coins === pkg.coins)?.id;
        if (!iapProductId) {
          toast.error("Prodotto non disponibile");
          return;
        }
        
        const result = await iapService.purchase(iapProductId);
        if (result.success) {
          // Validate on backend and credit coins
          const validation = await api.post('/shop/validate-iap', {
            product_id: iapProductId,
            transaction_id: result.transactionId,
            receipt: result.receipt,
          });
          toast.success(validation.data.message || "Acquisto completato!");
          onPurchaseComplete?.();
          onClose?.();
        } else if (result.error !== "cancelled") {
          toast.error("Acquisto non riuscito");
        }
      } else {
        // Stripe web flow
        const response = await api.post('/shop/create-checkout', {
          package_id: pkg.id,
          success_url: `${window.location.origin}/app/shop/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/app/shop/cancel`
        });
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel pagamento");
    } finally {
      setPurchasing(null);
    }
  };

  const handleRestore = async () => {
    if (!isNativeIOS) return;
    try {
      toast.info("Ripristino acquisti...");
      const purchases = await iapService.restorePurchases();
      if (purchases.length > 0) {
        const res = await api.post('/shop/restore-iap', { purchases });
        toast.success(`${res.data.restored_count} acquisti ripristinati!`);
        onPurchaseComplete?.();
      } else {
        toast.info("Nessun acquisto da ripristinare");
      }
    } catch (err) {
      toast.error("Errore nel ripristino");
    }
  };
  
  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(price);
  };
  
  // Package tier styling
  const getPackageTier = (goldenRequired, coins) => {
    if (goldenRequired >= 3) return { 
      style: "from-purple-100 via-indigo-100 to-purple-50 border-purple-400",
      badge: "Ultra Elite",
      badgeStyle: "from-purple-600 to-indigo-600"
    };
    if (goldenRequired >= 2) return { 
      style: "from-amber-100 via-yellow-100 to-amber-50 border-amber-400",
      badge: "Elite",
      badgeStyle: "from-amber-500 to-yellow-500"
    };
    if (goldenRequired >= 1) return { 
      style: "from-orange-100 via-red-50 to-orange-50 border-orange-400",
      badge: "Premium",
      badgeStyle: "from-orange-500 to-red-500"
    };
    if (coins >= 7000) return { 
      style: "from-yellow-50 to-amber-100 border-yellow-300",
      badge: "Miglior Valore",
      badgeStyle: "from-yellow-400 to-orange-500"
    };
    return { style: "from-gray-50 to-orange-50 border-orange-200", badge: null };
  };
  
  // Group packages by tier
  const groupedPackages = {
    base: packages.filter(p => p.golden_required === 0),
    premium: packages.filter(p => p.golden_required === 1),
    elite: packages.filter(p => p.golden_required === 2),
    ultra: packages.filter(p => p.golden_required === 3)
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg mx-auto bg-gradient-to-br from-white via-amber-50/30 to-orange-50/30 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <img src="/images/ade-coin.jpeg" alt="ADE" className="w-12 h-12 rounded-full object-cover bg-white dark:bg-black" />
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent font-bold">
              Acquista Crediti ADE
            </span>
          </DialogTitle>
          {isNativeIOS && (
            <button onClick={handleRestore} className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 mt-1">
              <RotateCcw className="w-3.5 h-3.5" />
              Ripristina acquisti
            </button>
          )}
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            </div>
          ) : (
            <>
              {/* Base Packages */}
              {groupedPackages.base.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Pacchetti Base
                  </h3>
                  <div className="space-y-2">
                    {groupedPackages.base.map((pkg) => (
                      <PackageCard 
                        key={pkg.id} 
                        pkg={pkg} 
                        onPurchase={handlePurchase}
                        purchasing={purchasing}
                        formatPrice={formatPrice}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Premium Packages - Golden Ring Lv.1 */}
              {groupedPackages.premium.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-orange-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Crown className="w-4 h-4" />
                    Premium (Gold Ring Lv.1+)
                  </h3>
                  <div className="space-y-2">
                    {groupedPackages.premium.map((pkg) => (
                      <PackageCard 
                        key={pkg.id} 
                        pkg={pkg} 
                        onPurchase={handlePurchase}
                        purchasing={purchasing}
                        formatPrice={formatPrice}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Elite Packages - Golden Ring Lv.2 */}
              {groupedPackages.elite.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-amber-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Crown className="w-4 h-4" />
                    Elite (Gold Ring Lv.2+)
                  </h3>
                  <div className="space-y-2">
                    {groupedPackages.elite.map((pkg) => (
                      <PackageCard 
                        key={pkg.id} 
                        pkg={pkg} 
                        onPurchase={handlePurchase}
                        purchasing={purchasing}
                        formatPrice={formatPrice}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Ultra Packages - Golden Ring Lv.3 */}
              {groupedPackages.ultra.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-purple-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Crown className="w-4 h-4" />
                    Ultra Elite (Gold Ring Lv.3)
                  </h3>
                  <div className="space-y-2">
                    {groupedPackages.ultra.map((pkg) => (
                      <PackageCard 
                        key={pkg.id} 
                        pkg={pkg} 
                        onPurchase={handlePurchase}
                        purchasing={purchasing}
                        formatPrice={formatPrice}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <p className="text-xs text-gray-500 text-center mt-4">
          Gli ADE Coins sono crediti interni della piattaforma, non costituiscono valuta, criptovaluta o strumento finanziario.
          <br />
          I pagamenti sono sicuri e gestiti da Stripe. I crediti acquistati non sono rimborsabili.
        </p>
      </DialogContent>
    </Dialog>
  );
};

// Package Card Component
const PackageCard = ({ pkg, onPurchase, purchasing, formatPrice }) => {
  const isLocked = !pkg.available;
  
  const getTierStyle = () => {
    if (pkg.golden_required >= 3) return "from-purple-100 to-indigo-100 border-purple-300";
    if (pkg.golden_required >= 2) return "from-amber-100 to-yellow-100 border-amber-300";
    if (pkg.golden_required >= 1) return "from-orange-100 to-red-50 border-orange-300";
    if (pkg.coins >= 7000) return "from-yellow-50 to-amber-100 border-yellow-300";
    return "from-gray-50 to-orange-50 border-orange-200";
  };
  
  return (
    <div
      className={`relative p-3 rounded-xl border-2 bg-gradient-to-r ${getTierStyle()} transition-all ${
        isLocked ? 'opacity-60' : 'hover:shadow-lg hover:scale-[1.01]'
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Logo ADE */}
        <div className="w-14 h-14 rounded-xl bg-white/80 flex items-center justify-center shadow-inner flex-shrink-0">
          <img src="/images/ade-coin.jpeg" alt="" className="w-10 h-10 rounded-full object-cover bg-white dark:bg-black" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-gray-800 text-lg">{formatCoins(pkg.coins)}</h3>
            {pkg.golden_required > 0 && (
              <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                pkg.golden_required >= 3 ? 'bg-purple-200 text-purple-700' :
                pkg.golden_required >= 2 ? 'bg-amber-200 text-amber-700' :
                'bg-orange-200 text-orange-700'
              }`}>
                Lv.{pkg.golden_required}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">Crediti ADE</p>
        </div>
        
        <Button
          onClick={() => onPurchase(pkg)}
          disabled={purchasing === pkg.id || isLocked}
          className={`font-bold px-4 py-2 rounded-xl shadow-lg min-w-[90px] ${
            isLocked 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
          }`}
        >
          {purchasing === pkg.id ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isLocked ? (
            <Lock className="w-4 h-4" />
          ) : (
            formatPrice(pkg.price, pkg.currency)
          )}
        </Button>
      </div>
      
      {/* Locked overlay message */}
      {isLocked && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-xl">
          <span className="bg-gray-800 text-white text-xs font-semibold px-3 py-1 rounded-full">
            🔒 Gold Ring Lv.{pkg.golden_required}
          </span>
        </div>
      )}
    </div>
  );
};

// Success page component
export const PurchaseSuccess = () => {
  const [status, setStatus] = useState('checking');
  const [result, setResult] = useState(null);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      checkStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, []);
  
  const checkStatus = async (sessionId) => {
    try {
      const response = await api.get(`/shop/checkout-status/${sessionId}`);
      setResult(response.data);
      setStatus(response.data.status === 'success' ? 'success' : 'pending');
    } catch (error) {
      setStatus('error');
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-amber-50/30 p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800">Verifica pagamento...</h2>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-200/50">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Pagamento Completato!</h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/images/ade-coin.jpeg" alt="" className="w-8 h-8 rounded-full object-cover bg-white dark:bg-black" />
              <span className="font-bold text-2xl text-amber-600">+{result?.coins_credited?.toLocaleString()}</span>
              <span className="text-gray-600">Crediti ADE</span>
            </div>
            <Button
              onClick={() => window.location.href = '/app/home'}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold px-8 py-3 rounded-xl"
            >
              Torna alla Home
            </Button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-red-400 to-pink-500 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Errore</h2>
            <p className="text-gray-600 mb-4">Si è verificato un errore con il pagamento.</p>
            <Button
              onClick={() => window.location.href = '/app/home'}
              variant="outline"
              className="px-8 py-3 rounded-xl"
            >
              Torna alla Home
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// Shop button for navigation
export const ShopButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl border border-amber-200 hover:shadow-md transition-all"
  >
    <ShoppingCart className="w-5 h-5 text-amber-600" />
    <span className="text-sm font-semibold text-amber-700">Shop</span>
  </button>
);

export default CoinShop;
